import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';

const ACTION_KEYS = ['resume_parsing', 'candidate_ranking', 'assessment_generation', 'ai_questions', 'video_interview'];
const ACTION_COLORS = {
  resume_parsing: '#00A0E0', candidate_ranking: '#F05000', assessment_generation: '#059669',
  ai_questions: '#D97706', video_interview: '#DC2626',
};
const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ label, value, hint, tone = '#002A38', testId }) {
  return (
    <Card data-testid={testId} style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 800, fontFamily: 'Cabinet Grotesk', color: tone, marginTop: '0.25rem', lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.375rem' }}>{hint}</div>}
    </Card>
  );
}

function exportCsv(data) {
  const labels = data.action_labels || {};
  const header = ['Company', 'Domain', 'Total Actions', 'Total Credits', 'Est Cost (USD)',
    ...ACTION_KEYS.map((k) => `${labels[k] || k} (credits)`)];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = (data.companies || []).map((c) => [
    c.company_slug, c.company_domain || '', c.actions, c.credits, (c.cost ?? 0).toFixed(2),
    ...ACTION_KEYS.map((k) => (c.by_action?.[k]?.credits ?? 0)),
  ]);
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chire-ai-usage-${data.month || 'all-time'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AiUsageTab() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [costInput, setCostInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await adminAPI.getAiUsage(m || undefined);
      setData(res.data);
      setCostInput(Number(res.data?.cost_per_credit ?? 0).toFixed(2));
    } catch (e) {
      toast.error('Failed to load AI usage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const saveCost = async () => {
    const v = parseFloat(costInput);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid cost per credit'); return; }
    setSaving(true);
    try {
      await adminAPI.updateAiUsageConfig({ cost_per_credit: v });
      toast.success('Cost per credit updated');
      await load(month);
    } catch (e) {
      toast.error('Failed to update cost');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <div style={{ color: '#64748B', padding: '2rem' }}>Loading AI usage…</div>;
  if (!data) return null;

  const totals = data.totals || {};
  const companies = data.companies || [];
  const byAction = data.by_action || {};
  const trend = data.trend || [];
  const cpc = data.cost_per_credit;

  return (
    <div data-testid="ai-usage-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 480, margin: 0, lineHeight: 1.6 }}>
          Internal view of AI consumption per company. Every AI action is logged with the company, recruiter,
          action type, timestamp and credits. Cost is estimated from the cost-per-credit below.
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.25rem' }}>Period</label>
            <select data-testid="ai-usage-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
              style={{ padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.8125rem' }}>
              <option value="">All time</option>
              {(data.months_available || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.25rem' }}>Cost / credit (USD)</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input data-testid="ai-usage-cost-input" type="number" step="0.01" min="0" value={costInput} onChange={(e) => setCostInput(e.target.value)}
                style={{ width: 100, padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.8125rem' }} />
              <Button data-testid="ai-usage-save-cost" onClick={saveCost} disabled={saving} style={{ borderRadius: 8, background: '#0076A3', color: '#fff' }}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
          <Button data-testid="ai-usage-export-csv" onClick={() => exportCsv(data)} variant="outline" style={{ borderRadius: 8 }}>Export CSV</Button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <Kpi testId="kpi-usage-companies" label="Companies" value={totals.companies ?? 0} hint={month || 'all time'} />
        <Kpi testId="kpi-usage-actions" label="Total AI actions" value={(totals.actions ?? 0).toLocaleString()} />
        <Kpi testId="kpi-usage-credits" label="Credits consumed" value={(totals.credits ?? 0).toLocaleString()} tone="#F05000" />
        <Kpi testId="kpi-usage-cost" label="Est. cost" value={money(totals.cost)} tone="#059669" hint={`@ ${money(cpc)}/credit`} />
      </div>

      {/* Month-over-month trend */}
      {trend.length > 0 && (
        <Card data-testid="ai-usage-trend" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>Month-over-month usage</h3>
          <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1rem' }}>Credits consumed across all companies — spot spikes at a glance.</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v, n) => [n === 'credits' ? `${v} credits` : v, n]}
                contentStyle={{ borderRadius: 10, border: '1px solid #ECE7DE', fontSize: 12 }} />
              <Bar dataKey="credits" name="credits" fill="#F05000" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Action-type breakdown */}
      {Object.keys(byAction).length > 0 && (
        <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>By action type</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {Object.entries(byAction).sort((a, b) => b[1].credits - a[1].credits).map(([at, v]) => (
              <div key={at} data-testid={`usage-action-${at}`} style={{ borderLeft: `3px solid ${ACTION_COLORS[at] || '#94A3B8'}`, paddingLeft: '0.75rem' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#002A38' }}>{v.label || at}</div>
                <div style={{ fontSize: '0.6875rem', color: '#64748B', marginTop: '0.2rem' }}>{v.actions.toLocaleString()} actions · {v.credits.toLocaleString()} credits · {money(v.cost)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Per-company table (expandable → recruiters) */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Usage by company{month ? ` — ${month}` : ''}</h3>
        {companies.length === 0 ? (
          <p data-testid="ai-usage-empty" style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>No AI usage recorded yet for this period.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748B', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.5rem' }}>Company</th>
                  <th style={{ padding: '0.5rem' }}>Actions</th>
                  <th style={{ padding: '0.5rem' }}>Credits</th>
                  <th style={{ padding: '0.5rem' }}>Est. cost</th>
                  <th style={{ padding: '0.5rem' }}>Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => {
                  const open = !!expanded[c.company_slug];
                  return (
                    <React.Fragment key={c.company_slug}>
                      <tr data-testid="ai-usage-company-row" onClick={() => setExpanded((p) => ({ ...p, [c.company_slug]: !p[c.company_slug] }))}
                        style={{ borderTop: '1px solid #F1F5F9', cursor: 'pointer' }}>
                        <td style={{ padding: '0.65rem 0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span data-testid="company-expand-toggle" style={{ color: '#94A3B8', fontSize: '0.7rem', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
                            <div>
                              <div style={{ fontWeight: 700, color: '#002A38' }}>{c.company_slug}</div>
                              {c.company_domain && <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{c.company_domain}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '0.65rem 0.5rem', fontWeight: 600 }}>{c.actions.toLocaleString()}</td>
                        <td style={{ padding: '0.65rem 0.5rem', fontWeight: 600, color: '#F05000' }}>{c.credits.toLocaleString()}</td>
                        <td style={{ padding: '0.65rem 0.5rem', fontWeight: 700, color: '#059669' }}>{money(c.cost)}</td>
                        <td style={{ padding: '0.65rem 0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                            {Object.entries(c.by_action || {}).map(([at, ba]) => (
                              <span key={at} title={`${ba.label}: ${ba.actions} actions · ${ba.credits} credits`}
                                style={{ fontSize: '0.625rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 999, background: `${ACTION_COLORS[at] || '#94A3B8'}14`, color: ACTION_COLORS[at] || '#64748B' }}>
                                {(ba.label || at).split(' ')[0]} {ba.actions}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr data-testid="company-recruiters-row" style={{ background: '#FAFAFF' }}>
                          <td colSpan={5} style={{ padding: '0.25rem 0.5rem 0.85rem 2rem' }}>
                            <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', margin: '0.5rem 0' }}>Recruiters driving usage</div>
                            {(c.recruiters || []).length === 0 ? (
                              <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>No recruiter attribution.</span>
                            ) : (
                              <table style={{ width: '100%', maxWidth: 640, borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                <tbody>
                                  {c.recruiters.map((r, i) => (
                                    <tr key={`${c.company_slug}-r${i}`} data-testid="recruiter-row" style={{ borderTop: '1px solid #EEF0F5' }}>
                                      <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: '#334155' }}>{r.recruiter_name}</td>
                                      <td style={{ padding: '0.35rem 0.5rem', color: '#64748B' }}>{r.actions} actions</td>
                                      <td style={{ padding: '0.35rem 0.5rem', color: '#F05000' }}>{r.credits} credits</td>
                                      <td style={{ padding: '0.35rem 0.5rem', fontWeight: 700, color: '#059669' }}>{money(r.cost)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
